import React, { useEffect, useState } from "react";
import api, { money } from "@/lib/api";
import { toast } from "sonner";
import { useI18n } from "@/context/PrefsContext";
import {
  PageHeader, PrimaryButton, Modal, Field, Input, Textarea, SelectBox, Button,
} from "@/components/ui-kit";
import { Plus, Sparkles, Trash2 } from "lucide-react";

const PALETTE = ["#7B2CBF", "#3B82F6", "#F97316", "#10B981", "#EC4899", "#1E293B", "#22C55E", "#F59E0B", "#64748B", "#8B5CF6", "#D4AF37"];
const EMPTY = { name: "", description: "", base_price: 0, duration: "2.5", color: "#7B2CBF", active: true };

export default function Services() {
  const [services, setServices] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editing, setEditing] = useState(null);
  const [q, setQ] = useState("");
  const { t } = useI18n();

  const load = () => api.get("/services").then(({ data }) => setServices(data));
  useEffect(() => { load(); }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const openNew = () => { setForm(EMPTY); setEditing(null); setOpen(true); };
  const openEdit = (s) => { setForm({ ...EMPTY, ...s }); setEditing(s.id); setOpen(true); };

  const save = async () => {
    if (!form.name) return toast.error(t("Service name is required"));
    const payload = { ...form, base_price: Number(form.base_price) };
    try {
      if (editing) await api.put(`/services/${editing}`, payload);
      else await api.post("/services", payload);
      toast.success(editing ? t("Service updated") : t("Service added"));
      setOpen(false); load();
    } catch { toast.error("Could not save service"); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this service?")) return;
    await api.delete(`/services/${id}`); toast.success(t("Service deleted")); load();
  };

  const filtered = services.filter((s) => s.name.toLowerCase().includes(q.toLowerCase()));

  return (
    <div data-testid="services-page">
      <PageHeader
        title="Services"
        subtitle="Pricing, duration, and color codes for every service you offer"
        search searchValue={q} onSearch={(e) => setQ(e.target.value)}
        action={<PrimaryButton onClick={openNew} data-testid="add-service-button"><Plus size={16} /> {t("Add Custom Service")}</PrimaryButton>}
      />

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6" data-testid="services-grid">
        {filtered.map((s, i) => {
          const color = s.color || PALETTE[i % PALETTE.length];
          return (
            <div key={s.id} className="relative bg-white rounded-2xl shadow-[0_8px_30px_-12px_rgba(90,24,154,0.25)] border border-[#f3eefb] overflow-hidden flex flex-col hover:shadow-[0_14px_38px_-12px_rgba(90,24,154,0.35)] hover:-translate-y-1 transition-all duration-300 group">
              <div className="h-1.5 w-full" style={{ background: color }} />
              <div className="p-6 flex flex-col flex-1">
                <div className="w-14 h-14 rounded-full flex items-center justify-center mb-5" style={{ background: `${color}1f`, color }}>
                  <Sparkles size={24} strokeWidth={1.8} />
                </div>
                <h3 className="font-display text-xl font-bold text-[#1a1523]">{s.name}</h3>

                <div className="mt-5">
                  <div className="text-[11px] font-semibold uppercase tracking-wider text-[#9b8bb5]">{t("Starting At")}</div>
                  <div className="flex items-center justify-between mt-1">
                    <div className="text-3xl font-extrabold text-[#5A189A] font-display">
                      {s.base_price > 0 ? money(s.base_price).replace(".00", "") : t("Custom")}
                    </div>
                    <span className="px-3 py-1 rounded-full text-[11px] font-bold uppercase tracking-wide" style={{ background: s.active === false ? "#f1f1f4" : "#F1E9FB", color: s.active === false ? "#94909c" : "#5A189A" }}>
                      {s.active === false ? t("Inactive") : t("Active")}
                    </span>
                  </div>
                </div>

                <div className="border-t border-[#f3eefb] mt-5 pt-4 flex items-center justify-between">
                  <span className="text-sm text-[#6b647a]">~{s.duration || "2.5"} {t("hr avg")}</span>
                  <div className="flex items-center gap-3">
                    <button onClick={() => remove(s.id)} className="text-[#cbb8e4] hover:text-red-500 transition opacity-0 group-hover:opacity-100" title="Delete"><Trash2 size={15} /></button>
                    <button onClick={() => openEdit(s)} className="text-sm font-semibold text-[#5A189A] hover:text-[#D4AF37] transition" data-testid={`edit-service-${s.id}`}>{t("Edit")}</button>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <Modal open={open} onClose={() => setOpen(false)} title={editing ? "Edit Service" : "Add Custom Service"} size="md"
        footer={<><Button variant="outline" onClick={() => setOpen(false)}>{t("Cancel")}</Button><PrimaryButton onClick={save} data-testid="save-service-button">{t("Save Service")}</PrimaryButton></>}>
        <div className="space-y-4">
          <Field label="Service Name *"><Input value={form.name} onChange={(e) => set("name", e.target.value)} data-testid="service-name-input" /></Field>
          <Field label="Description"><Textarea rows={2} value={form.description} onChange={(e) => set("description", e.target.value)} /></Field>
          <div className="grid grid-cols-2 gap-4">
            <Field label="Starting Price ($)"><Input type="number" value={form.base_price} onChange={(e) => set("base_price", e.target.value)} /></Field>
            <Field label="Avg Duration (hrs)"><Input value={form.duration} onChange={(e) => set("duration", e.target.value)} /></Field>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <Field label="Color Code">
              <div className="flex items-center gap-2">
                <input type="color" value={form.color} onChange={(e) => set("color", e.target.value)} className="w-10 h-10 rounded-lg border border-[#e7ddf5] cursor-pointer" />
                <Input value={form.color} onChange={(e) => set("color", e.target.value)} className="flex-1" />
              </div>
            </Field>
            <Field label="Status"><SelectBox value={form.active ? "Active" : "Inactive"} onChange={(e) => set("active", e.target.value === "Active")} options={["Active", "Inactive"]} /></Field>
          </div>
        </div>
      </Modal>
    </div>
  );
}
